// ============================================================
// Footer — eye-catching, dense, brand-anchored
// ============================================================

import { Link } from 'wouter';
import { BrandMark } from './Wordmark';
import { ArrowUpRight, Mail, Twitter, Linkedin } from 'lucide-react';

const YEAR = new Date().getFullYear();

export default function Footer() {
  return (
    <footer className="relative isolate overflow-hidden border-t border-ink-3 bg-ink-2 text-ash">
      {/* gradient top accent line */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(124,255,184,0.6), transparent)',
        }}
        aria-hidden
      />
      {/* atmospheric backdrops */}
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-40" aria-hidden />
      <div
        className="absolute -bottom-40 -left-40 -z-10 h-[500px] w-[500px] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />
      <div
        className="absolute -top-40 right-1/4 -z-10 h-[400px] w-[400px] rounded-full opacity-[0.04] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="mx-auto max-w-content px-6 py-14 md:px-12 md:py-16">
        {/* TOP — Brand banner with stats inline */}
        <div className="grid items-center gap-10 border-b border-ink-3 pb-12 md:grid-cols-12 md:gap-12 md:pb-14">
          {/* Brand */}
          <div className="md:col-span-7">
            <div className="font-mono text-mono-xs uppercase text-signal">
              // a wavefront studio company
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 sm:gap-4">
              <BrandMark size={40} />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-3xl font-bold leading-none tracking-headline-tight text-white sm:text-4xl md:text-5xl lg:text-[3.5rem]">
                  WFS&nbsp;Chatbot
                </span>
                <span
                  className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 font-mono text-[0.625rem] font-semibold tracking-mono"
                  style={{ background: '#7CFFB8', color: '#0E1316' }}
                >
                  AI
                </span>
              </div>
            </div>
            <p className="mt-5 max-w-md text-sm text-ash">
              Customer messaging for shops that mean business. Built by the team behind Resin Rock,
              the #1 resin bound system in North America.
            </p>
          </div>

          {/* Stats column */}
          <div className="md:col-span-5">
            <div className="grid grid-cols-3 gap-4">
              <FooterStat value="7" label="channels" />
              <FooterStat value="< 2s" label="avg reply" />
              <FooterStat value="24/7" label="always on" />
            </div>
            <div className="mt-5 flex items-center gap-2 rounded-input border border-signal/20 px-3 py-2"
                 style={{ background: 'rgba(124,255,184,0.05)' }}>
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
              </span>
              <span className="font-mono text-mono-xs uppercase text-chalk">
                all systems operational
              </span>
              <span className="ml-auto font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                live
              </span>
            </div>
          </div>
        </div>

        {/* MIDDLE — nav columns */}
        <div className="grid gap-10 py-12 md:grid-cols-12 md:py-14">
          {/* Tagline column */}
          <div className="md:col-span-5">
            <div className="font-mono text-mono-xs uppercase text-graphite">
              // sitemap
            </div>
            <p className="mt-4 max-w-sm text-sm text-ash">
              Every link, every page, every legal document. If you can&apos;t find something,
              the email below goes to a real human.
            </p>
            <a
              href="mailto:info@wavefrontstudiollc.com"
              className="group/cta mt-5 inline-flex items-center gap-2 rounded-pill border border-ink-3 bg-ink/60 px-4 py-2.5 text-sm font-medium text-white transition-all hover:border-signal/40"
            >
              <Mail size={14} className="text-signal" />
              info@wavefrontstudiollc.com
              <ArrowUpRight
                size={13}
                className="text-ash transition-transform group-hover/cta:-translate-y-0.5 group-hover/cta:translate-x-0.5"
              />
            </a>
          </div>

          {/* Product column */}
          <div className="md:col-span-2">
            <div className="font-mono text-mono-xs uppercase text-signal">// product</div>
            <nav className="mt-4 flex flex-col gap-3 text-sm">
              <a href="/#product" className="text-ash transition-colors hover:text-white">Product</a>
              <a href="/#channels" className="text-ash transition-colors hover:text-white">Channels</a>
              <a href="/#how-it-works" className="text-ash transition-colors hover:text-white">How it works</a>
              <a href="/#pricing" className="text-ash transition-colors hover:text-white">Pricing</a>
              <a href="/#faq" className="text-ash transition-colors hover:text-white">FAQ</a>
            </nav>
          </div>

          {/* Access column */}
          <div className="md:col-span-2">
            <div className="font-mono text-mono-xs uppercase text-signal">// access</div>
            <nav className="mt-4 flex flex-col gap-3 text-sm">
              <Link href="/request-access" className="text-ash transition-colors hover:text-white">
                Request access
              </Link>
              <a href="/" className="text-ash transition-colors hover:text-white">Sign in</a>
              <a
                href="https://resinrock.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-ash transition-colors hover:text-white"
              >
                Parent brand
                <ArrowUpRight size={12} />
              </a>
            </nav>
          </div>

          {/* Legal column */}
          <div className="md:col-span-3">
            <div className="font-mono text-mono-xs uppercase text-signal">// legal</div>
            <nav className="mt-4 flex flex-col gap-3 text-sm">
              <Link href="/legal/terms" className="text-ash transition-colors hover:text-white">
                Terms of Service
              </Link>
              <Link href="/legal/privacy" className="text-ash transition-colors hover:text-white">
                Privacy Policy
              </Link>
              <Link href="/legal/dpa" className="text-ash transition-colors hover:text-white">
                Data Processing Addendum
              </Link>
              <Link href="/data-deletion" className="text-ash transition-colors hover:text-white">
                Data Deletion
              </Link>
            </nav>
          </div>
        </div>

        {/* BOTTOM strip */}
        <div className="flex flex-col items-start gap-4 border-t border-ink-3 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-6">
            <span className="font-mono text-mono-xs uppercase text-graphite">
              © {YEAR} Wavefront Studio LLC. All rights reserved.
            </span>
          </div>

          {/* Social icons */}
          <div className="flex items-center gap-2">
            <a
              href="https://twitter.com/resinrock"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter / X"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-ink-3 bg-ink/40 text-ash transition-all hover:border-signal/40 hover:text-signal"
            >
              <Twitter size={13} />
            </a>
            <a
              href="https://linkedin.com/company/resinrock"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-ink-3 bg-ink/40 text-ash transition-all hover:border-signal/40 hover:text-signal"
            >
              <Linkedin size={13} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-input border border-ink-3 bg-ink/40 p-3">
      <div className="text-2xl font-bold tracking-headline-tight text-white">{value}</div>
      <div className="mt-1 font-mono text-[0.5625rem] uppercase tracking-mono text-graphite">
        {label}
      </div>
    </div>
  );
}
